import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function RefundPolicy() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "intro", title: "1. Overview" },
    { id: "cancellation", title: "2. Cancellation Policy & Windows" },
    { id: "refund-eligibility", title: "3. Refund Eligibility" },
    { id: "processing-timeline", title: "4. Mode & Timelines of Refund" },
    { id: "rescheduling", title: "5. Rescheduling Policy" },
    { id: "contact", title: "6. Support & Refund Help" },
  ];

  return (
    <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
      <LegalHeader />

      {/* Header Title Section */}
      <section className="border-b border-gray-200 py-10 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs text-gray-500 mb-2">Fixezy / Legal / Refund Policy</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Cancellation & Refund Policy
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated: October 2026 • Applicable to Fixezy Private Limited services and app bookings
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
              Table of Contents
            </p>
            <nav className="flex flex-col space-y-2">
              {sections.map((section) => (
                <p
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                      ? "text-black font-semibold"
                      : "text-gray-600 hover:text-black"
                    }`}
                >
                  {section.title}
                </p>
              ))}
            </nav>
          </aside>

          {/* Legal Text Area */}
          <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <section id="intro" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                1. Overview
              </h2>
              <p>
                At Fixezy Private Limited ("Fixezy", "we", "us", or "our"), customer satisfaction and fair business practices are our priority. This Cancellation & Refund Policy outlines the terms governing booking cancellations, fee deductions (if any), and refund timelines for services booked through the Fixezy mobile app or website (fixezy.in).
              </p>
            </section>

            {/* Section 2 */}
            <section id="cancellation" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                2. Cancellation Policy & Windows
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>
                  <strong>Free Cancellation:</strong> You can cancel your scheduled service booking free of charge up to <strong>2 hours prior</strong> to the start of the scheduled appointment window.
                </li>
                <li>
                  <strong>Late Cancellation Fee:</strong> If a cancellation is requested after a service partner has been dispatched or within 30 minutes of the scheduled timeslot, a nominal convenience fee (up to ₹99) may be charged to compensate the partner's travel expenses.
                </li>
                <li>
                  <strong>Partner Inability to Attend:</strong> If Fixezy or the assigned technician is unable to fulfill your booking due to unforeseen circumstances, your booking will either be rescheduled at your convenience or cancelled with a 100% full refund.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="refund-eligibility" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                3. Refund Eligibility
              </h2>
              <p>You are eligible for a refund under the following circumstances:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>You cancelled a prepaid booking within the eligible free cancellation window.</li>
                <li>A prepaid booking could not be serviced due to technician unavailability or operational constraints.</li>
                <li>You were erroneously charged twice for the same booking.</li>
                <li>Upon diagnostic inspection, an appliance is determined to be non-repairable due to obsolete or discontinued parts. In such cases, only the standard diagnostic fee applies, and any pre-collected repair balance is refunded in full.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="processing-timeline" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                4. Mode & Timelines of Refund
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>
                  <strong>Online Payments (UPI, Cards, Netbanking):</strong> Refunds are processed back to the original source payment account (bank account, debit/credit card, or UPI ID) through RBI-regulated payment gateways within <strong>5 to 7 business days</strong>.
                </li>
                <li>
                  <strong>Cash Payments:</strong> For any overpayment or scope reduction during cash-settled visits, verified refunds are transferred directly via UPI to your registered mobile number within 24 to 48 hours.
                </li>
                <li>
                  Fixezy will notify you via SMS / push notification once the refund transaction has been initiated.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="rescheduling" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                5. Rescheduling Policy
              </h2>
              <p>
                You can easily reschedule your booking to any available slot without any penalty up to 2 hours before the scheduled time directly through the Fixezy app.
              </p>
            </section>

            {/* Section 6 */}
            <section id="contact" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                6. Support & Refund Help
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                If your refund has not reflected within the expected timeline, please reach out with your Booking ID:
              </p>
              <div className="border border-gray-200 rounded-lg p-4 space-y-1 text-sm text-gray-800">
                <p><strong>Fixezy Private Limited</strong></p>
                <p>Vijayanagar, Bengaluru, Karnataka - 560040, India</p>
                <p>Email: support@fixezy.in</p>
                <p>Support Hours: Mon – Fri, 9:00 AM – 6:00 PM IST</p>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
